/**
 * Workflow detail — the CLIENT-SAFE half of the contract: the range/tab
 * vocabulary and the row shapes `/agents/workflows/[slug]` renders.
 *
 * Why this file exists: `_studio.tsx` is a client component, and it needs
 * `DETAIL_RANGES` as a VALUE. Importing that from `workflow-detail.ts` pulled
 * `optimization/db.ts` → `optimization/config.ts` → `import "server-only"` into
 * the client graph, which is not a warning — it is a whole-app compile error
 * that 500s EVERY route in dev until it's fixed (found by driving the QA
 * toolkit at the workflow-detail surface, #44046).
 *
 * The rail: nothing in this module may import a module that reaches the DB.
 * Type-only imports are erased by the bundler and so are safe; a value import
 * is not. Server loaders live in `workflow-detail.ts`, which re-exports
 * everything here so existing server call sites are unchanged.
 */

import type { LoopStepFailures } from "./db";
import type { WorkflowRunsByDayRow } from "../agents/db";
import type { MeasuredLayer, ValueAssumptions, WorkflowValue } from "./value-model";

// ─── Range ───────────────────────────────────────────────────────────────────

export type DetailRangeKey = "1d" | "7d" | "30d" | "all";

export const DETAIL_RANGES: { key: DetailRangeKey; label: string; days: number | null }[] = [
  { key: "1d", label: "1 day", days: 1 },
  { key: "7d", label: "7 days", days: 7 },
  { key: "30d", label: "30 days", days: 30 },
  { key: "all", label: "All time", days: null },
];

export function parseDetailRange(raw: string | null | undefined): DetailRangeKey {
  if (raw === "1d" || raw === "7d" || raw === "30d" || raw === "all") return raw;
  return "30d";
}

export function rangeDays(range: DetailRangeKey): number | null {
  return DETAIL_RANGES.find((r) => r.key === range)?.days ?? 30;
}

// ─── Tabs ────────────────────────────────────────────────────────────────────

export type DetailTabKey = "overview" | "pipeline" | "runs" | "optimize" | "done";

export function parseDetailTab(raw: string | null | undefined): DetailTabKey {
  if (
    raw === "overview" ||
    raw === "pipeline" ||
    raw === "runs" ||
    raw === "optimize" ||
    raw === "done"
  ) {
    return raw;
  }
  // Accept prototype alias
  if (raw === "optimization") return "optimize";
  return "overview";
}

// ─── Row shapes the studio renders ───────────────────────────────────────────

export interface RoleHealth {
  role: string;
  runs: number;
  avg_cost: number;
  avg_tokens: number;
  avg_wall_ms: number;
}

export interface LoopRunHealth {
  total: number;
  completed: number;
  /** status='running' right now (not windowed — current snapshot). */
  runningNow: number;
  passRate: number;
  avg_cost_usd: number;
  avg_wall_ms: number;
  byRole: RoleHealth[];
}

export interface StepTelemetry {
  id: number;
  ordinal: number;
  name: string;
  rung: number;
  role: string | null;
  model: string | null;
  runs: number;
  avg_cost: number;
  avg_tokens: number;
  avg_wall_ms: number;
  failures: LoopStepFailures;
  /**
   * `excellent_loop_steps.config`, verbatim JSON — the step's authored control
   * flow, including `{"onError":{"step":"heal"}}`.
   *
   * ── Why this field was added (RO-3-02b) ──
   *
   * The engine has honored `onError` for a long time and it is mutation-tested
   * (`loop-runner.ts` `pickRepair` / run-repair-then-rejoin / the
   * `invalid-on-error` pre-flight; `test-loop-runner-retry.mjs`,
   * `test-loop-runner.mjs`). What has never existed is anything that DRAWS it:
   * `planTopology` emits a typed `TopologyEdge{kind:"on_error"}` and has zero UI
   * call sites, and the Pipeline tab is a flat `steps.map` rail. See
   * `docs/rollover/REACT-ROLLOVER-PRD.md` §11.1.
   *
   * Adding the renderer is the PEER program's W2-A2, not the rollover's (§11.2a,
   * §11.3's UI-surfaces class). But §11.2(b) does put the CONTRACT WIDENING on
   * the rollover — "do it once, in the contract, and both programs consume it" —
   * and this is that widening: the branch data now reaches the client on the same
   * payload the Pipeline tab already renders, so W2-A2 is a renderer change with
   * no endpoint change behind it. Without this field it would have had to widen
   * the contract first, which is precisely the double work §11.3 exists to stop.
   *
   * Raw JSON, deliberately not parsed here: the engine owns that shape and a
   * second parser is a second place for it to drift.
   */
  config: string;
}

export interface OverviewWindow {
  range: DetailRangeKey;
  days: number | null;
  value: WorkflowValue;
  /** Convenience projection of the windowed value layer for hero tiles. */
  valueView: {
    measured: MeasuredLayer;
    projected: {
      time_saved_hours: number;
      time_saved_usd: number;
      issues_avoided_usd: number;
      gross_value_usd: number;
      net_value_usd: number;
      roi: number | null;
    };
    annual: { runs_per_day: number; net_value_usd: number };
    assumptions: ValueAssumptions;
  };
  runHealth: LoopRunHealth;
  daily: WorkflowRunsByDayRow[];
}

// ─── Done ────────────────────────────────────────────────────────────────────

/** One live Definition-of-Done criterion, shaped like `readTaskDod()` output
 *  (RECURSIVE-DONE-PRD.md §13). `binding:false` is a proposal that surfaces but
 *  does not gate. */
export interface DodFactView {
  label: string;
  pass: boolean;
  proof?: string;
  source?: string;
  binding?: boolean;
}

/** The Done card's live payload. `undefined` at the call site means "no
 *  criteria to show for this workflow", not "the read failed". */
export interface WorkflowDoneFacts {
  criteria: DodFactView[];
  subtreeDone?: boolean;
}
